import {
  Button,
  Card,
  CreateButton,
  Divider,
  Icons,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Space,
  Table,
  Typography,
  Upload,
  UploadFile,
} from '@pankod/refine-antd';
import { UploadChangeParam } from 'antd/lib/upload';
import { CustomDrawer } from 'components';
import React, { useEffect, useState } from 'react';
import { Action, ActionKind, IPurchasedProductState } from 'reducers/product-purchase-reducer';
import { Column, usePurchaseSaleCsvImport } from '../../hooks/usePurchaseSaleCsvImport';
import { AddProductForm } from './AddProductForm';
import { EditProductForm } from './EditProductForm';
import styles from './style.module.css';

type ProductPurchaseTableProps = {
  purchasedProducts: IPurchasedProductState[];
  warehouse: string | undefined;
  vat: number;
  shipment: number;
  discountCost: number;
  paidInCash: boolean;
  payNow: boolean;
  fullDue: boolean;
  productDispatcher: React.Dispatch<Action>;
};

const { Text, Title } = Typography;

export const ProductPurchaseTable: React.FC<ProductPurchaseTableProps> = (props) => {
  const [editPurchasedProduct, setEditPurchasedProduct] = useState<IPurchasedProductState | null>(
    null,
  );
  const [isAddDrawerVisible, setIsAddDrawerVisible] = useState<boolean>(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState<boolean>(false);
  const [discountPercentage, setDiscountPercentage] = useState<number | null>(null);
  const [flatDiscount, setFlatDiscount] = useState<number | null>(null);
  const { isImporting, getCsvTemplate, onImportCsv } = usePurchaseSaleCsvImport();

  const productTotals = props.purchasedProducts.map(
    (product) => product.quantity * product.unitPrice,
  );
  const subTotal = productTotals.reduce(
    (previousValue, currentValue) => previousValue + currentValue,
    0,
  );

  useEffect(() => {
    if (discountPercentage !== null) {
      const discountAmount = Number((subTotal * (discountPercentage / 100)).toFixed(2));
      props.productDispatcher({ type: ActionKind.SetDiscount, payload: discountAmount });
    }
    if (props.discountCost) {
      setFlatDiscount(props.discountCost);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountPercentage, props.discountCost]);

  useEffect(() => {
    if (flatDiscount !== null) {
      props.productDispatcher({ type: ActionKind.SetDiscount, payload: flatDiscount });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatDiscount]);

  const onFullCashCheckboxInputChange = (e: RadioChangeEvent) => {
    if (e.target.checked) {
      props.productDispatcher({ type: ActionKind.SetPayNow, payload: false });
      props.productDispatcher({ type: ActionKind.SetDue, payload: false });
    }
    props.productDispatcher({ type: ActionKind.SetPaidInCash, payload: e.target.checked });
  };

  const onFullDueCheckboxInputChange = (e: RadioChangeEvent) => {
    if (e.target.checked) {
      props.productDispatcher({ type: ActionKind.SetPaidInCash, payload: false });
      props.productDispatcher({ type: ActionKind.SetPayNow, payload: false });
    }
    props.productDispatcher({ type: ActionKind.SetDue, payload: e.target.checked });
  };

  const footer = () => {
    const vatPercentage = props.vat;
    const shipmentCost = props.shipment;
    const discountCost = props.discountCost;
    const vatAmount = Number((subTotal * (vatPercentage / 100)).toFixed(2));
    const netTotal = Number((subTotal + vatAmount + shipmentCost).toFixed(2));

    const discountAmount = Number((subTotal * ((discountPercentage || 0) / 100)).toFixed(2));
    const netTotalWithDiscount = Number((subTotal - discountCost).toFixed(2));

    return (
      <div className={styles['product-footer']}>
        <Text>Sub-Total: {subTotal.toFixed(2)}</Text>
        <Divider />

        <div style={{ display: 'none' }}>
          <Text>Vat: </Text>
          <InputNumber
            min={0}
            value={vatPercentage}
            defaultValue={0}
            formatter={(value) => `${value}%`}
            onChange={(value) =>
              props.productDispatcher({ type: ActionKind.SetVat, payload: value ?? 0 })
            }
          />
          <Text style={{ marginLeft: '1rem' }}>{vatAmount}</Text>
        </div>

        <div style={{ marginTop: '1rem', display: 'none' }}>
          <Text>Shipment: </Text>
          <InputNumber
            min={0}
            value={shipmentCost}
            defaultValue={0}
            onChange={(value) =>
              props.productDispatcher({ type: ActionKind.SetShipment, payload: value ?? 0 })
            }
          />
        </div>

        <div style={{ display: 'flex' }}>
          <div style={{ display: 'none' }}>
            <Text>Discount: </Text>
            <InputNumber
              min={0}
              value={discountPercentage}
              formatter={(value) => `${value}%`}
              onChange={(value) => setDiscountPercentage(value)}
              disabled={!discountPercentage && !!flatDiscount}
            />
            <Text style={{ marginLeft: '1rem' }}>{discountAmount}</Text>
          </div>
          {window.location.pathname.includes('create') && (
            <div style={{ marginLeft: '1rem', paddingTop: '0.3rem' }}>
              <Radio onChange={onFullCashCheckboxInputChange} checked={props.paidInCash} />
              <Text style={{ marginRight: '0.5rem' }}> Full Cash </Text>
            </div>
          )}
          <div style={{ marginLeft: '1rem', paddingTop: '0.3rem' }}>
            <Radio onChange={onFullDueCheckboxInputChange} checked={props.fullDue} />
            <Text style={{ marginRight: '0.5rem' }}> Full Due </Text>
          </div>

          <div style={{ marginLeft: '1rem' }}>
            <Text style={{ marginRight: '0.5rem' }}> Discount: </Text>
            <InputNumber
              min={0}
              value={flatDiscount}
              onChange={(value) => {
                setFlatDiscount(value ?? 0);
              }}
              disabled={!!discountPercentage && !flatDiscount}
              placeholder="Value"
            />
          </div>
        </div>

        <Divider />
        <Text style={{ visibility: 'hidden' }}>Net-Total: {netTotal}</Text>
        <Text>Net-Total: {netTotalWithDiscount}</Text>
      </div>
    );
  };

  const handleFileUpload = async (info: UploadChangeParam<UploadFile<any>>) => {
    if (!info.fileList.length) return;
    const file = info.fileList[0].originFileObj!;
    const variantsInfo = await onImportCsv(file);
    props.productDispatcher({
      type: ActionKind.SetPurchasedProducts,
      payload: variantsInfo,
    });
  };

  const cardTitle = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        width: '100%',
        whiteSpace: 'normal',
      }}
    >
      <Title level={4} style={{ margin: 0 }}>
        Add product
      </Title>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <CreateButton type="primary" onClick={() => setIsAddDrawerVisible(true)}>
          Add New
        </CreateButton>

        <Button onClick={getCsvTemplate} icon={<Icons.DownloadOutlined />}>
          Sample CSV
        </Button>

        <Upload
          accept="text/csv"
          maxCount={1}
          beforeUpload={() => false}
          onChange={handleFileUpload}
          onRemove={() =>
            props.productDispatcher({ type: ActionKind.SetPurchasedProducts, payload: [] })
          }
        >
          <Button icon={<Icons.ImportOutlined />} loading={isImporting}>
            Import
          </Button>
        </Upload>
      </div>
    </div>
  );

  return (
    <>
      {/* Add Drawer Form */}
      <CustomDrawer
        title="Add Product"
        visible={isAddDrawerVisible}
        onClose={() => setIsAddDrawerVisible(false)}
      >
        <AddProductForm
          purchasedProducts={props.purchasedProducts}
          onSubmit={(product) =>
            props.productDispatcher({
              type: ActionKind.AddPurchasedProduct,
              payload: product,
            })
          }
        />
      </CustomDrawer>

      {/* Edit Drawer Form */}
      {editPurchasedProduct && (
        <CustomDrawer
          title="Edit Product"
          visible={isEditDrawerVisible}
          onClose={() => setIsEditDrawerVisible(false)}
        >
          <EditProductForm
            editProduct={editPurchasedProduct}
            onSubmit={(product) => {
              props.productDispatcher({
                type: ActionKind.EditPurchasedProduct,
                payload: product,
              });
              setIsEditDrawerVisible(false);
            }}
          />
        </CustomDrawer>
      )}

      <Card title={cardTitle}>
        <Table
          dataSource={props.purchasedProducts}
          rowKey="sku"
          scroll={{ x: true }}
          footer={(_data) => footer()}
        >
          <Table.Column dataIndex="sku" title={Column.Sku} />
          <Table.Column dataIndex="productVariantName" title={Column.Name} />
          <Table.Column dataIndex="quantity" title={Column.Quantity} />
          <Table.Column dataIndex="unitPrice" title={Column.UnitPrice} />
          <Table.Column<IPurchasedProductState>
            dataIndex="subTotal"
            title="Total"
            render={(_, record) => {
              return (
                <Text>{Number.parseFloat((record.quantity * record.unitPrice).toFixed(2))}</Text>
              );
            }}
          />
          <Table.Column<IPurchasedProductState>
            title="Actions"
            dataIndex="actions"
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <Button
                    size="small"
                    icon={<Icons.EditOutlined />}
                    onClick={() => {
                      setIsEditDrawerVisible(true);
                      setEditPurchasedProduct(record);
                    }}
                  />
                  <Button
                    size="small"
                    danger
                    icon={<Icons.DeleteOutlined />}
                    onClick={() =>
                      props.productDispatcher({
                        type: ActionKind.RemovePurchasedProduct,
                        payload: record.productVariantId,
                      })
                    }
                  />
                </Space>
              );
            }}
          />
        </Table>
      </Card>
    </>
  );
};
